import { FEATURE_KEY } from '@adtrackify/at-tracking-event-types';

import { Navigate } from 'react-router-dom';

// Keep non-page components as regular imports
import FocusedFeatureShell from '@/app/templates/focused-feature-shell/focused-feature-shell';
import { FOCUSED_FEATURE_SHELL_VARIANT } from '@/app/templates/focused-feature-shell/types/focused-feature-shell-enums';
import LoggedInWrapper from '@/app/templates/logged-in-wrapper';
import AuthHeader from '@/app/templates/auth-header';
import PageContainer from '@/components/layout/page-container';
import { FeatureGateOverlay } from '@/features/billing/components/feature-gates/feature-gate-overlay';
import { ShopifyRouteGuard } from '@/routes/guards/shopify-route-guard';
import { BILLING_DESTINATION_LEGACY } from '@/lib/navigation/constants/billing-destination-constants';
import { BillingV2SplitRedirect } from '@/features/billing/components/billing-v2-split-redirect';
import { PreserveLocationNavigate } from '@/app/shell/preserve-location-navigate';
import { CUSTOMER_HOME_PATH } from '@/lib/navigation/constants/post-auth-destination-constants';
import { searchParamsLoader } from '@/lib/navigation/search-params-loader';
import { EventDestinations, Orders, OrderDetails, Products, DestinationConfigPage, DevelopmentPage, AccountPage, PasswordPage, BillingSettings, InvoicesPage, PlansSettingsPage, UsageSettingsPage, PaymentMethodsSettingsPage, Acquisition, DiscountedSubscriptionPlan, Cohorts, Onboarding, GetStartedRoute, EventDebugger, ShopifySubscriptionPlans, ShopifyPlanConfirm, ShopifySubscriptionUpdate, StripePlanSelection, StripePlanConfirm, StripePlanSuccess, AuthCallback, SettingsLayout, TeamManagementPage, NotificationsPage, DashboardSettingsPage, NotificationCenterPage, AttributionV2, HomePage, HomeV2Page, Ecommerce, PnlDashboard, IncomeStatementPage, HomeDashboard, KPIDashboardPage, CustomDashboard, TrafficOverview, DataQualityDashboard, EventsManagerPage, IntegrationsPage, ShopifySetup, BillingRecoveryPage, CostsPage, ApiKeysPage, UtmTrackingPage, PixelSettingsStubPage, CustomEventMappingStubPage, RollingReachDashboard, ReachComparisonDashboard, CumulativeReachDashboard, RealtimeDashboard } from './protected-route-pages';

export function protectedRoutes(
  _isContactAcquisitionFlagEnabled: boolean | undefined,
  _isFbIntegrationFlagEnabled = false
) {
  return [
    {
      element: <LoggedInWrapper />,
      loader: searchParamsLoader,
      children: [
        {
          path: 'onboarding',
          element: (
            <PageContainer>
              <Onboarding />
            </PageContainer>
          ),
        },
        {
          path: 'get-started',
          element: <GetStartedRoute />,
        },
        {
          path: 'home',
          element: (
            <PageContainer>
              <HomePage />
            </PageContainer>
          ),
        },
        {
          path: 'homev2',
          element: (
            <PageContainer>
              <HomeV2Page />
            </PageContainer>
          ),
        },
        {
          path: 'board/views',
          element: (
            <PageContainer>
              <CustomDashboard />
            </PageContainer>
          ),
        },
        {
          path: 'dashboard/home',
          element: <Navigate replace to="/board/views" />,
        },
        {
          path: 'views',
          element: <Navigate replace to="/board/views" />,
        },
        {
          path: 'event-health',
          element: <Navigate replace to="/events-manager" />,
        },
        {
          path: 'pixel/destinations',
          element: <Navigate replace to="/destinations" />,
        },
        {
          path: 'pixel/event-destinations',
          element: <Navigate replace to="/destinations" />,
        },
        {
          path: 'destinations',
          element: (
            <PageContainer>
              <EventDestinations />
            </PageContainer>
          ),
        },
        {
          path: 'destinations/new',
          element: (
            <PageContainer>
              <DestinationConfigPage />
            </PageContainer>
          ),
        },
        {
          path: 'destinations/:destinationId',
          element: (
            <PageContainer>
              <DestinationConfigPage />
            </PageContainer>
          ),
        },
        {
          path: 'custom-triggers',
          element: <Navigate replace to="/destinations" />,
        },
        {
          path: 'board/attribution',
          element: (
            <PageContainer>
              <AttributionV2 />
            </PageContainer>
          ),
        },
        {
          path: 'dashboard/attribution',
          element: <Navigate replace to="/board/attribution" />,
        },
        {
          path: 'pixel/integrations',
          element: <Navigate replace to="/integrations" />,
        },
        {
          path: 'integrations',
          element: (
            <PageContainer>
              <IntegrationsPage />
            </PageContainer>
          ),
        },
        {
          path: 'setup/shopify',
          element: (
            <PageContainer>
              <ShopifySetup />
            </PageContainer>
          ),
        },
        {
          path: 'pixel/events-debugger',
          element: (
            <PageContainer>
              <EventDebugger />
            </PageContainer>
          ),
        },
        {
          path: 'board/kpis',
          element: (
            <PageContainer>
              <KPIDashboardPage />
            </PageContainer>
          ),
        },
        {
          path: 'dashboard/kpis',
          element: <Navigate replace to="/board/kpis" />,
        },
        {
          path: 'board/home-dashboard',
          element: (
            <PageContainer>
              <HomeDashboard />
            </PageContainer>
          ),
        },
        {
          path: 'dashboard/home-dashboard',
          element: <Navigate replace to="/board/home-dashboard" />,
        },
        {
          path: 'board/traffic-overview',
          element: (
            <PageContainer>
              <TrafficOverview />
            </PageContainer>
          ),
        },
        {
          path: 'dashboard/traffic-overview',
          element: <Navigate replace to="/board/traffic-overview" />,
        },
        {
          path: 'meta-data-quality',
          element: (
            <PageContainer>
              <DataQualityDashboard />
            </PageContainer>
          ),
        },
        {
          path: 'meta-data-quality/event/:eventName',
          element: (
            <PageContainer>
              <DataQualityDashboard />
            </PageContainer>
          ),
        },
        {
          path: 'events-manager',
          element: (
            <PageContainer>
              <EventsManagerPage />
            </PageContainer>
          ),
        },
        {
          path: 'events-manager/event/:eventName',
          element: (
            <PageContainer>
              <EventsManagerPage />
            </PageContainer>
          ),
        },
        {
          path: 'data-quality',
          element: <Navigate replace to="/events-manager" />,
        },
        {
          path: 'data-quality/event/:eventName',
          element: <Navigate replace to="/events-manager" />,
        },
        {
          path: 'dashboard/rolling-reach',
          element: <Navigate replace to="/board/reach/rolling" />,
        },
        {
          path: 'dashboard/reach-comparison',
          element: <Navigate replace to="/board/reach/comparison" />,
        },
        {
          path: 'dashboard/cumulative-reach',
          element: <Navigate replace to="/board/reach/incremental" />,
        },
        {
          path: 'dashboard/reach/rolling',
          element: <Navigate replace to="/board/reach/rolling" />,
        },
        {
          path: 'dashboard/reach/comparison',
          element: <Navigate replace to="/board/reach/comparison" />,
        },
        {
          path: 'dashboard/reach/incremental',
          element: <Navigate replace to="/board/reach/incremental" />,
        },
        {
          path: 'board/reach/rolling',
          element: (
            <PageContainer>
              <FeatureGateOverlay
                description="Analyze your rolling reach metrics to understand campaign impact over time."
                featureKey={FEATURE_KEY.INCREMENTAL_REACH}
                featureName="Incremental Reach"
                preview
                visiblePercent={40}
              >
                <RollingReachDashboard />
              </FeatureGateOverlay>
            </PageContainer>
          ),
        },
        {
          path: 'board/reach/comparison',
          element: (
            <PageContainer>
              <FeatureGateOverlay
                description="Compare reach across campaigns and time periods to find what drives growth."
                featureKey={FEATURE_KEY.INCREMENTAL_REACH}
                featureName="Incremental Reach"
                preview
                visiblePercent={40}
              >
                <ReachComparisonDashboard />
              </FeatureGateOverlay>
            </PageContainer>
          ),
        },
        {
          path: 'board/reach/incremental',
          element: (
            <PageContainer>
              <FeatureGateOverlay
                description="Track cumulative reach to measure the true incremental impact of your spend."
                featureKey={FEATURE_KEY.INCREMENTAL_REACH}
                featureName="Incremental Reach"
                preview
                visiblePercent={40}
              >
                <CumulativeReachDashboard />
              </FeatureGateOverlay>
            </PageContainer>
          ),
        },
        {
          path: 'board/cohorts',
          element: (
            <ShopifyRouteGuard>
              <PageContainer>
                <Cohorts />
              </PageContainer>
            </ShopifyRouteGuard>
          ),
        },
        {
          path: 'dashboard/cohorts',
          element: <Navigate replace to="/board/cohorts" />,
        },
        {
          path: 'board/ecommerce',
          element: (
            <ShopifyRouteGuard>
              <PageContainer>
                <Ecommerce />
              </PageContainer>
            </ShopifyRouteGuard>
          ),
        },
        ...(import.meta.env.VITE_APP_REALTIME_DASHBOARD === 'TRUE'
          ? [
              {
                path: 'board/realtime',
                element: (
                  <PageContainer>
                    <FeatureGateOverlay
                      description="Monitor your store's activity as it happens with live visitor tracking and event streams."
                      featureKey={FEATURE_KEY.REAL_TIME_ANALYTICS}
                      featureName="Real-Time Analytics"
                      preview
                      visiblePercent={40}
                    >
                      <RealtimeDashboard />
                    </FeatureGateOverlay>
                  </PageContainer>
                ),
              },
            ]
          : []),
        {
          path: 'dashboard/ecommerce',
          element: <Navigate replace to="/board/ecommerce" />,
        },
        {
          path: 'board/pnl',
          element: <Navigate replace to="/board/pnl/income-statement" />,
        },
        {
          path: 'board/pnl/income-statement',
          element: (
            <ShopifyRouteGuard>
              <PageContainer>
                <IncomeStatementPage />
              </PageContainer>
            </ShopifyRouteGuard>
          ),
        },
        {
          path: 'board/pnl/dashboard',
          element: (
            <ShopifyRouteGuard>
              <PageContainer>
                <PnlDashboard />
              </PageContainer>
            </ShopifyRouteGuard>
          ),
        },
        ...(import.meta.env.VITE_APP_BETA_FEATURE === 'TRUE'
          ? [
              {
                path: 'board/orders',
                element: (
                  <ShopifyRouteGuard>
                    <PageContainer>
                      <Orders />
                    </PageContainer>
                  </ShopifyRouteGuard>
                ),
              },
              {
                path: 'board/orders/:orderId',
                element: (
                  <ShopifyRouteGuard>
                    <PageContainer>
                      <OrderDetails />
                    </PageContainer>
                  </ShopifyRouteGuard>
                ),
              },
              {
                path: 'board/products',
                element: (
                  <ShopifyRouteGuard>
                    <PageContainer>
                      <Products />
                    </PageContainer>
                  </ShopifyRouteGuard>
                ),
              },
            ]
          : []),
        ...(import.meta.env.VITE_APP_CONTACT_ACQUISITION_ENABLED === 'TRUE'
          ? [
              {
                path: 'account/acquisition',
                element: (
                  <FocusedFeatureShell variant={FOCUSED_FEATURE_SHELL_VARIANT.FEATURE}>
                    <Acquisition />
                  </FocusedFeatureShell>
                ),
              },
            ]
          : []),
        {
          path: 'notifications',
          element: (
            <PageContainer>
              <NotificationCenterPage />
            </PageContainer>
          ),
        },
        // Redirect /settings/user to user profile settings
        {
          path: 'settings/user',
          element: <Navigate replace to="/account/settings/password" />,
        },
        {
          path: 'account/settings',
          element: <SettingsLayout />,
          children: [
            { index: true, element: <Navigate replace to="account" /> },
            { path: 'account', element: <AccountPage /> },
            { path: 'password', element: <PasswordPage /> },
            // Back-compat: previous "Profile" page is replaced by Account + Password.
            { path: 'profile', element: <Navigate replace to="account" /> },
            { path: 'dashboard', element: <DashboardSettingsPage /> },
            { path: 'utm', element: <UtmTrackingPage /> },
            { path: 'pixel', element: <PixelSettingsStubPage /> },
            { path: 'custom-event-mapping', element: <CustomEventMappingStubPage /> },
            { path: 'team', element: <TeamManagementPage /> },
            { path: 'billing', element: <BillingSettings /> },
            { path: 'billing/v2', element: <BillingV2SplitRedirect /> },
            { path: 'usage', element: <UsageSettingsPage /> },
            { path: 'payment-methods', element: <PaymentMethodsSettingsPage /> },
            { path: 'invoices', element: <InvoicesPage /> },
            { path: 'plans', element: <PlansSettingsPage /> },
            { path: 'notifications', element: <NotificationsPage /> },
            { path: 'api-keys', element: <ApiKeysPage /> },
            ...(import.meta.env.VITE_APP_COST_SETTINGS_ENABLED === 'TRUE'
              ? [
                  { path: 'costs', element: <CostsPage /> },
                  {
                    path: 'costs/product-defaults',
                    element: <Navigate replace to="/account/settings/costs?section=defaults" />,
                  },
                  {
                    path: 'costs/products',
                    element: <Navigate replace to="/account/settings/costs?section=products" />,
                  },
                  {
                    path: 'costs/order-costs',
                    element: <Navigate replace to="/account/settings/costs?section=order" />,
                  },
                  {
                    path: 'costs/gateways',
                    element: <Navigate replace to="/account/settings/costs?section=gateways" />,
                  },
                  {
                    path: 'costs/shipping',
                    element: <Navigate replace to="/account/settings/costs?section=shipping" />,
                  },
                  {
                    path: 'costs/variable',
                    element: <Navigate replace to="/account/settings/costs?section=variable" />,
                  },
                  {
                    path: 'costs/fixed',
                    element: <Navigate replace to="/account/settings/costs?section=fixed" />,
                  },
                  {
                    path: 'costs/goods',
                    element: <Navigate replace to="/account/settings/costs?section=defaults" />,
                  },
                  {
                    path: 'costs/expenses',
                    element: <Navigate replace to="/account/settings/costs?section=variable" />,
                  },
                ]
              : []),
          ],
        },
        {
          path: 'account/billing',
          element: <PreserveLocationNavigate pathname={BILLING_DESTINATION_LEGACY} />,
        },
        {
          path: 'account/billing-recovery',
          element: (
            <FocusedFeatureShell variant={FOCUSED_FEATURE_SHELL_VARIANT.INTERRUPT}>
              <BillingRecoveryPage />
            </FocusedFeatureShell>
          ),
        },
        {
          path: 'account/stripe-plan/success',
          element: (
            <PageContainer>
              <StripePlanSuccess />
            </PageContainer>
          ),
        },
        {
          // Always-on alias; tab query remaps via commercial-split redirect.
          path: 'settings/billing/v2',
          element: <BillingV2SplitRedirect />,
        },
        {
          path: 'shopify/subscription-updated',
          element: (
            <PageContainer>
              <ShopifySubscriptionUpdate />
            </PageContainer>
          ),
        },
        {
          path: '/',
          element: <Navigate replace to={CUSTOMER_HOME_PATH} />,
        },
        {
          path: '*',
          element: <DevelopmentPage />,
        },
      ],
    },
    {
      path: '/auth/callback',
      element: <AuthCallback />,
    },
    {
      element: <AuthHeader />,
      children: [
        {
          path: 'account/shopify-plan',
          element: <ShopifySubscriptionPlans newUser={false} type="SHOPIFY" />,
        },
        {
          path: 'account/shopify-plan/confirm',
          element: <ShopifyPlanConfirm />,
        },
        {
          path: 'account/shopify-plan/discount',
          element: <DiscountedSubscriptionPlan />,
        },
      ],
    },
    {
      element: <AuthHeader />,
      children: [
        {
          path: 'account/stripe-plan',
          element: <StripePlanSelection />,
        },
      ],
    },
    {
      element: <AuthHeader />,
      children: [
        {
          path: 'account/stripe-plan/confirm',
          element: <StripePlanConfirm />,
        },
      ],
    },
  ];
}
